import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'src/app/service/auth.service';
import { LcmmDataSource } from 'src/app/datasource/lcmm-data-source';
import { Sort } from 'src/app/datasource/page';
import { Sensorstation } from 'sensorstation-lib-js';
import { Group } from 'lcmm-lib-js';
import { Observable } from 'rxjs';
import { MatSort, Sort as MatSortEvent } from '@angular/material/sort';
import {
  ConfirmationDialogAction,
  ConfirmationDialogComponent,
  ConfirmationDialogData,
  ConfirmationDialogEntity,
} from 'src/app/confirmation-dialog/confirmation-dialog.component';
import { NavigationComponent } from 'src/app/navigation/navigation.component';
import { FooterComponent } from 'src/app/footer/footer.component';
import { ResponsiveDesignService } from 'src/app/service/responsiveDesign.service';
import {
  NetworkService,
  NetworkStateEvent,
} from 'src/app/service/network.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EnvConfigurationService } from 'src/app/service/env-config.service';
import {
  SensorstationQuery,
  SensorstationService,
} from '../service/sensorstation.service';
import { SensorstationCreateDialogComponent } from '../sensorstation-create-dialog/sensorstation-create-dialog.component';
import { SensorstationDialogComponent } from '../sensorstation-dialog/sensorstation-dialog.component';
import { SensorstationDetailsComponent } from '../sensorstation-details/sensorstation-details.component';

@Component({
  selector: 'app-sensorstations-list',
  templateUrl: './sensorstations-list.component.html',
  styleUrls: ['./sensorstations-list.component.scss'],
  standalone: false,
})
export class SensorstationsListComponent implements OnInit, OnDestroy {
  private className = 'SensorstationsListComponent';

  public displayedColumns: string[] = [
    'sensorstationName',
    'sensorstationId',
    'sensorstationGroup',
    'streetName',
    'cityName',
    'details',
  ];

  public groups: Group[] = [];

  public filteredGroups: Observable<Group[]>;

  public groupFilter = '';

  public selectedGroup: Group;

  private currentlyDisplayedSensorstation: Sensorstation;

  public initialSort: Sort = { property: 'deviceId', order: 'asc' };

  public sensorstationData: LcmmDataSource<Sensorstation, SensorstationQuery>;

  public deviceIdFilter = '';

  public deviceNameFilter = '';

  private originalOwner = 'TELEKOM';

  public allSensorstations: Sensorstation[] = [];

  // --- UI filter state for client-side table filters ---
  public filters: {
    query: string;
    name: string;
    deviceId: string;
    group: string;
    street: string;
    city: string;
  } = { query: '', name: '', deviceId: '', group: '', street: '', city: '' };

  // helper to normalize values for case-insensitive matching
  private _norm(v: unknown): string {
    return (v ?? '').toString().toLowerCase();
  }

  // called by filter inputs
  public onFilterChange(): void {
    this.pageIndex = 0;
    this.updatePagedData();
  }

  public clearFilters(): void {
    this.filters = {
      query: '',
      name: '',
      deviceId: '',
      group: '',
      street: '',
      city: '',
    };
    this.deviceNameFilter = '';
    this.deviceIdFilter = '';
    this.groupFilter = '';
    this.pageIndex = 0;
    this.updatePagedData();
  }

  public get filteredSensorstations(): Sensorstation[] {
    return this.pagedSensorstations;
  }

  public pagedSensorstations: Sensorstation[] = [];

  public pageSize = 10;

  public pageIndex = 0;

  public totalItems = 0;

  public sortState: MatSortEvent = { active: 'name', direction: 'asc' };

  // --- Downloads (frontend-only) state ---
  public fromTime = '00:00';

  public toTime = '23:59';

  public fromDate: Date | null = null;

  public toDate: Date | null = null;

  public expandedRow: Sensorstation | null = null;

  public isDownloading = false;

  public downloadOptions = {
    aggregationTime: 'HOURLY' as
      | 'FINEST'
      | 'HOURLY'
      | 'DAILY'
      | 'MONTHLY'
      | null,
    from: null as Date | null,
    to: null as Date | null,
    valuesMap: {} as Record<string, boolean>,
  };

  // Toggle to inspect API responses in DevTools instead of downloading a file
  private DEBUG_DOWNLOAD = false;

  public currentDownloadRow: Sensorstation | null = null;

  public aggregationSelectionDisabled = false;

  private isTSystemsGroup(group?: string | null): boolean {
    return (group || '').toLowerCase().includes('t-systems');
  }

  public allowFieldSelectionFor(row?: Sensorstation | null): boolean {
    if (!row) return false;
    return this.isTSystemsGroup(row.group);
  }

  public allowAggregationSelectionFor(row?: Sensorstation | null): boolean {
    if (!row) return false;
    return this.isTSystemsGroup(row.group);
  }

  @ViewChild(MatSort) public sort: MatSort;

  @ViewChild(MatPaginator) public paginator: MatPaginator;

  @ViewChild('downloadsDialog') public downloadsDialog!: TemplateRef<any>;

  constructor(
    public authService: AuthService,
    public dialog: MatDialog,
    public sensorstationService: SensorstationService,
    private cdr: ChangeDetectorRef,
    private responsiveDesignService: ResponsiveDesignService,
    private http: HttpClient,
    private envService: EnvConfigurationService,
    public networkService: NetworkService
  ) {
    this.sensorstationData = new LcmmDataSource<
      Sensorstation,
      SensorstationQuery
    >(
      'Sensorstation',
      (request, query) => this.sensorstationService.page(request, query),
      this.initialSort,
      {
        deviceId: undefined,
        aFESerialNumber: undefined,
        name: undefined,
        group: undefined,
        street: undefined,
        city: undefined,
        state: undefined,
      },
      this.networkService
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  private nsCallback(eventType: NetworkStateEvent, msg?: any): void {
    switch (eventType) {
      case NetworkStateEvent.CLIENTERROR:
      case NetworkStateEvent.SERVERERROR:
        this.paginator.pageIndex -= 1;
        break;
      default:
        break;
    }
  }

  ngOnInit(): void {
    this.networkService.register(this.className, this.nsCallback.bind(this));
    this.sensorstationService.getAllSensorstations().subscribe((data) => {
      this.allSensorstations = data;
      this.pageIndex = 0;
      this.updatePagedData();
    });
  }

  ngOnDestroy(): void {
    this.networkService.deregister(this.className);
    NavigationComponent.setDisabled(false);
    FooterComponent.setDisabled(false);
    this.responsiveDesignService.deregister(this.className);
  }

  public openEdit(
    sensorstation: Sensorstation,
    allowEditing: boolean,
    event?: MouseEvent
  ): void {
    if (event) {
      event.stopPropagation();
    }

    const isAdmin = this.authService.isSensorstationAdmin();
    const isEditor =
      this.authService.isSensorstationEditor() && this.isOwner(sensorstation); // adjust as needed for subgroups

    if (!isAdmin && !isEditor) {
      return;
    }

    const dialogRef = this.dialog.open(SensorstationDialogComponent, {
      width: '80%',
      data: { sensorstation, groups: this.groups, allowEditing: true },
    });

    dialogRef.afterClosed().subscribe((isChanged) => {
      if (isChanged != null && isChanged === true) {
        this.sensorstationData.fetch();
        this.sensorstationService.getAllSensorstations().subscribe((data) => {
          this.allSensorstations = data;
          this.pageIndex = 0;
          this.updatePagedData();
        });
      }
    });
  }

  public create(): void {
    if (!this.authService.isSensorstationAdmin()) {
      return;
    }
    const dialogRef = this.dialog.open(SensorstationCreateDialogComponent, {
      width: '80%',
      height: '80%',
      data: {
        groups: this.groups,
      },
    });

    dialogRef.afterClosed().subscribe((returnedSensorstation) => {
      if (returnedSensorstation != null) {
        this.sensorstationData.fetch();
      }
    });
  }

  private alterSensorstation(
    theSensorstation: Sensorstation,
    theAction: ConfirmationDialogAction,
    func: (s: Sensorstation) => Observable<Sensorstation>
  ) {
    const dialogData: ConfirmationDialogData = {
      entity: ConfirmationDialogEntity.sensorstation,
      action: theAction,
      data: theSensorstation.name,
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '80%',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((shoudlAlter) => {
      if (shoudlAlter) {
        func(theSensorstation).subscribe(() => {
          this.sensorstationData.fetch();
        });
      }
    });
  }

  public deleteSensorstation(
    sensorstation: Sensorstation,
    event?: MouseEvent
  ): void {
    if (
      !this.authService.isSensorstationAdmin() &&
      !(this.authService.isSensorstationEditor() && this.isOwner(sensorstation))
    ) {
      return;
    }
    if (event) {
      event.stopPropagation();
    }
    const alterFunction = this.sensorstationService.deleteSensorstation.bind(
      this.sensorstationService
    );
    this.alterSensorstation(
      sensorstation,
      ConfirmationDialogAction.delete,
      alterFunction
    );
  }

  public isOwner(sensorstation: Sensorstation): boolean {
    return sensorstation.owner === this.originalOwner;
  }

  public showDetails(sensorstation: Sensorstation): void {
    this.currentlyDisplayedSensorstation = sensorstation;
    const dialogRef = this.dialog.open(SensorstationDetailsComponent, {
      width: '80%',
      height: '80%',
      data: {
        sensorstation,
      },
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  public openDownloadsDialog(row: Sensorstation, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    // Track current row and prepare field selection rules based on group
    this.currentDownloadRow = row;

    // If field selection is NOT allowed (non-T-Systems groups), enforce ALL_FIELDS preselection
    if (!this.allowFieldSelectionFor(row)) {
      this.downloadOptions.valuesMap = { ALL_FIELDS: true };
    } else {
      // Reset to allow user-driven selection for T-Systems groups
      this.downloadOptions.valuesMap = {};
    }

    // Enforce aggregation rule: only T‑Systems may change aggregation time
    if (!this.allowAggregationSelectionFor(row)) {
      // Non‑T‑Systems: fixed HOURLY
      this.downloadOptions.aggregationTime = 'HOURLY';
      this.aggregationSelectionDisabled = true;
    } else {
      // T‑Systems: require explicit user choice → unset initially
      this.downloadOptions.aggregationTime = null;
      this.aggregationSelectionDisabled = false;
    }

    const dialogRef = this.dialog.open(this.downloadsDialog, {
      width: '900px',
      maxHeight: '80vh',
      data: { row },
    });

    dialogRef.afterClosed().subscribe(() => {
      // Reset all dialog-scoped values to defaults
      this.downloadOptions = {
        aggregationTime: 'HOURLY',
        from: null,
        to: null,
        valuesMap: {},
      };
      this.fromDate = null;
      this.toDate = null;
      this.fromTime = '00:00';
      this.toTime = '23:59';
      this.isDownloading = false;
      this.currentDownloadRow = null;
      this.aggregationSelectionDisabled = false;
    });
  }

  public queryByFilter(): void {
    // Legacy entry point – just refresh paging based on current filters
    this.pageIndex = 0;
    this.updatePagedData();
  }

  private _filterArray(arr: Sensorstation[]): Sensorstation[] {
    const f = this.filters;
    const q = this._norm(f.query);
    const devName = (this.deviceNameFilter || '').toLowerCase();
    const devId = (this.deviceIdFilter || '').toLowerCase();
    const grp = (this.groupFilter || '').toLowerCase();

    return (arr || []).filter((row: any) => {
      const name = this._norm(row?.name);
      const id = this._norm(row?.deviceId);
      const group = this._norm(row?.group);
      const street = this._norm(row?.location?.street);
      const house = this._norm(row?.location?.houseNumber);
      const city = this._norm(row?.location?.city);

      const contains = (val: string, needle: string) =>
        !needle || val.includes(needle.toLowerCase());
      const matches = (val: string, needle: string) =>
        !needle || val.includes(this._norm(needle));

      const queryHit =
        !q ||
        [name, id, group, `${street} ${house} ${city}`].some(
          (v) => v && v.includes(q)
        );

      const uiFiltersOk =
        matches(name, f.name) &&
        matches(id, f.deviceId) &&
        matches(group, f.group) &&
        matches(street, f.street) &&
        matches(city, f.city);

      const legacyOk =
        contains(name, devName) && contains(id, devId) && contains(group, grp);

      return queryHit && uiFiltersOk && legacyOk;
    });
  }

  public updatePagedData(): void {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;

    const fullFiltered = this._filterArray(this.allSensorstations);

    // Apply client-side sorting based on current sort state
    const active = this.sortState?.active;
    const direction = this.sortState?.direction || 'asc';

    const getVal = (row: any, key?: string): any => {
      switch (key) {
        case 'name':
          return row?.name ?? '';
        case 'deviceId':
          return row?.deviceId ?? '';
        case 'group':
          return row?.group ?? '';
        case 'street':
          return row?.location?.street ?? '';
        case 'city':
          return row?.location?.city ?? '';
        default:
          return '';
      }
    };

    if (active && direction) {
      fullFiltered.sort((a, b) => {
        const va = getVal(a, active);
        const vb = getVal(b, active);

        const aNum =
          // eslint-disable-next-line no-nested-ternary
          typeof va === 'number'
            ? va
            : Number.isFinite(Number(va))
            ? Number(va)
            : null;
        const bNum =
          // eslint-disable-next-line no-nested-ternary
          typeof vb === 'number'
            ? vb
            : Number.isFinite(Number(vb))
            ? Number(vb)
            : null;

        let cmp: number;
        if (aNum !== null && bNum !== null) {
          cmp = aNum - bNum;
        } else {
          const sa = (va ?? '').toString().toLowerCase();
          const sb = (vb ?? '').toString().toLowerCase();
          cmp = sa.localeCompare(sb);
        }
        return direction === 'asc' ? cmp : -cmp;
      });
    }

    this.totalItems = fullFiltered.length;
    this.pagedSensorstations = fullFiltered.slice(start, end);
  }

  public onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagedData();
  }

  public onSortChange(event: MatSortEvent): void {
    this.sortState = event;
    this.pageIndex = 0;
    this.updatePagedData();
  }

  // ===== Frontend-only Downloads =====
  public toggleDownloads(row: Sensorstation, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.openDownloadsDialog(row, event);
  }

  private toIsoWithTime(
    dateLike: Date | string | null | undefined,
    time: string | null | undefined,
    mode: 'start' | 'end'
  ): string | null {
    if (!dateLike) return null;
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return null;
    if (time && /^\d{2}:\d{2}$/.test(time)) {
      const [hh, mm] = time.split(':').map((x) => parseInt(x, 10));
      d.setHours(hh, mm, 0, 0);
    } else if (mode === 'start') {
      d.setHours(0, 0, 0, 0);
    } else {
      d.setHours(23, 59, 59, 999);
    }
    return d.toISOString();
  }

  private isValidTimeString(t?: string | null): boolean {
    return !!t && /^\d{2}:\d{2}$/.test(t);
  }

  public isDownloadFormComplete(): boolean {
    // Must have a selected row (set when opening the dialog)
    if (!this.currentDownloadRow) return false;

    // Dates required
    if (!this.fromDate || !this.toDate) return false;

    // Times required and must be valid
    if (
      !this.isValidTimeString(this.fromTime) ||
      !this.isValidTimeString(this.toTime)
    )
      return false;

    // from must be <= to
    const fromIso = this.toIsoWithTime(this.fromDate, this.fromTime, 'start');
    const toIso = this.toIsoWithTime(this.toDate, this.toTime, 'end');
    if (!fromIso || !toIso) return false;
    if (new Date(fromIso).getTime() > new Date(toIso).getTime()) return false;

    // Additional requirements for T‑Systems groups
    const isTSys = this.allowAggregationSelectionFor(this.currentDownloadRow);
    if (isTSys) {
      // aggregation time must be chosen (UI provides a default, but keep check)
      if (this.downloadOptions.aggregationTime == null) return false;

      // at least one value must be selected
      const anyValueSelected = Object.values(
        this.downloadOptions.valuesMap || {}
      ).some((v) => v === true);
      if (!anyValueSelected) return false;
    }

    return true;
  }

  public startDownloadWithOptions(row: Sensorstation): void {
    let selected = Object.entries(this.downloadOptions.valuesMap)
      .filter(([, v]) => !!v)
      .map(([k]) => k);

    // Enforce rule: only T-Systems groups may choose specific fields; others must use ALL_FIELDS
    if (!this.allowFieldSelectionFor(row)) {
      selected = ['ALL_FIELDS'];
    }

    if (!row?.locationId || !row?.group) {
      return;
    }

    const haveFrom = !!(this.fromDate || this.downloadOptions.from);
    const haveTo = !!(this.toDate || this.downloadOptions.to);
    if (!haveFrom || !haveTo) {
      return;
    }

    if (selected.length === 0) {
      return;
    }

    // If ALL_FIELDS is selected, only send that one to the API
    const values = selected.includes('ALL_FIELDS') ? ['ALL_FIELDS'] : selected;

    const fromIso = this.toIsoWithTime(
      this.fromDate ?? this.downloadOptions.from,
      this.fromTime,
      'start'
    );
    const toIso = this.toIsoWithTime(
      this.toDate ?? this.downloadOptions.to,
      this.toTime,
      'end'
    );

    // Determine effective aggregation time (non‑T‑Systems forced to HOURLY)
    const effectiveAggregationTime = this.allowAggregationSelectionFor(row)
      ? this.downloadOptions.aggregationTime
      : 'HOURLY';

    // --- Filename helpers: ASCII transliteration + compact time format ---
    const transliterate = (s: string) =>
      (s || '')
        .normalize('NFD')
        .replace(/ß/g, 'ss')
        .replace(/ẞ/g, 'SS')
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/Ä/g, 'Ae')
        .replace(/Ö/g, 'Oe')
        .replace(/Ü/g, 'Ue')
        .replace(/[\u0300-\u036f]/g, ''); // strip remaining diacritics

    const sanitizeForFilename = (s: string) =>
      transliterate(s)
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^A-Za-z0-9._-]/g, '_');

    const formatIsoForFilename = (iso: string) => {
      const d = new Date(iso);
      const pad = (n: number) => String(n).padStart(2, '0');
      // Use UTC because toIsoWithTime() returns UTC ISO strings
      const yyyy = d.getUTCFullYear();
      const mm = pad(d.getUTCMonth() + 1);
      const dd = pad(d.getUTCDate());
      const hh = pad(d.getUTCHours());
      const mi = pad(d.getUTCMinutes());
      // Result: YYYY-MM-DD_HH-MM
      return `${yyyy}-${mm}-${dd}_${hh}-${mi}`;
    };

    const sensorNameOrId = String(
      (row as any).name ||
        (row as any).deviceId ||
        (row as any).locationId ||
        'sensor'
    );

    // Normalize aggregation time if UI value includes a namespace like "SENSORSTATION.HOURLY"
    const normalizeAggregationTime = (v: string): string => {
      const t = (v || '').split('.').pop() || '';
      return t.toUpperCase(); // -> HOURLY, DAILY, MONTHLY, ...
    };

    const suggestedBaseName = `${sanitizeForFilename(
      sensorNameOrId
    )}_${normalizeAggregationTime(
      effectiveAggregationTime
    )}_${formatIsoForFilename(fromIso!)}_${formatIsoForFilename(toIso!)}`;

    this.isDownloading = true;
    const cleanGroup = row.group || '';

    let url = `${
      this.envService.config.sensorstationManagementUrl
    }/measurements/average/${encodeURIComponent(cleanGroup)}/${row.locationId}`;

    const params = new URLSearchParams();
    params.set(
      'aggregationTime',
      normalizeAggregationTime(effectiveAggregationTime)
    );
    if (fromIso) params.set('from', fromIso);
    if (toIso) params.set('to', toIso);

    const sanitizeParam = (s: string) =>
      (s || '').trim().replace(/^'+|'+$/g, '');
    const cleanedValues = values.map(sanitizeParam);
    const aggVals = cleanedValues.includes('ALL_FIELDS')
      ? ['ALL_FIELDS']
      : cleanedValues;

    // build final URL: base params first, then aggregationValues at the very end (explicit order)
    const baseQuery = params.toString();
    let finalUrl = `${url}?${baseQuery}`;
    aggVals.forEach((v) => {
      finalUrl += `&aggregationValues=${encodeURIComponent(v)}`;
    });

    url = finalUrl;

    const headers = new HttpHeaders({
      Accept: 'application/json, text/csv;q=0.9, */*;q=0.8',
    });
    console.debug('[download] final URL', url);

    if (this.DEBUG_DOWNLOAD) {
      // Debug mode: fetch as text to inspect in DevTools
      this.http
        .get(url, {
          responseType: 'text' as const,
          headers,
          observe: 'response',
        })
        .subscribe({
          next: (res) => {
            // eslint-disable-next-line no-console
            console.debug(
              '[download][DEBUG] status',
              res.status,
              res.statusText
            );
          },
          complete: () => {
            this.isDownloading = false;
          },
        });
      return;
    }

    // Normal mode: prefer JSON → CSV conversion; fallback to passthrough blob
    this.http
      .get(url, { responseType: 'blob' as const, headers, observe: 'response' })
      .subscribe({
        next: (res) => {
          const contentType = res.headers.get('Content-Type') || '';

          // Helper to trigger browser download from text
          const triggerDownload = (text: string) => {
            const blobOut = new Blob(['\ufeff', text], {
              // <— UTF-8 BOM
              type: 'text/csv;charset=utf-8',
            });
            const a = document.createElement('a');
            const objectUrl = URL.createObjectURL(blobOut);
            a.href = objectUrl;
            a.download = `${suggestedBaseName}.csv`;
            a.click();
            URL.revokeObjectURL(objectUrl);
            this.isDownloading = false;
          };

          // If server returns JSON -> convert to CSV
          if (contentType.includes('application/json')) {
            res.body
              .text()
              .then((jsonText) => {
                let parsed: any;
                try {
                  parsed = JSON.parse(jsonText);
                } catch (e) {
                  // If JSON parsing fails, just save original as CSV with one column
                  triggerDownload(jsonText);
                  return;
                }
                const csv = this.jsonToCsv(parsed);
                triggerDownload(csv);
              })
              .catch(() => {
                // As a last resort, pass through the blob
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(res.body);
                a.href = objectUrl;
                a.download = `${suggestedBaseName}.csv`;
                a.click();
                URL.revokeObjectURL(objectUrl);
                this.isDownloading = false;
              });
            return;
          }

          // If the server already returned CSV (or anything else) → passthrough
          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(res.body);
          a.href = objectUrl;
          a.download = `${suggestedBaseName}.csv`;
          a.click();
          URL.revokeObjectURL(objectUrl);
          this.isDownloading = false;
        },
        error: () => {
          this.isDownloading = false;
        },
      });
  }

  private flattenObject(
    obj: any,
    prefix = ''
  ): Record<string, string | number | boolean | null> {
    const out: Record<string, string | number | boolean | null> = {};
    if (obj === null || obj === undefined) {
      out[prefix || 'value'] = null;
      return out;
    }
    if (typeof obj !== 'object') {
      out[prefix || 'value'] = obj as any;
      return out;
    }
    const isDate = obj instanceof Date;
    if (isDate) {
      out[prefix || 'value'] = (obj as Date).toISOString();
      return out;
    }
    const entries = Array.isArray(obj) ? obj.entries() : Object.entries(obj);
    // eslint-disable-next-line no-restricted-syntax
    for (const [kRaw, v] of entries as any) {
      const k = String(kRaw);
      const key = prefix ? `${prefix}.${k}` : k;
      if (v !== null && typeof v === 'object' && !(v instanceof Date)) {
        Object.assign(out, this.flattenObject(v, key));
      } else {
        out[key] = v as any;
      }
    }
    return out;
  }

  private jsonToCsv(data: any): string {
    // Normalize to an array of records. Try common wrappers first.
    let records: any[] = [];
    if (Array.isArray(data)) {
      records = data;
    } else if (Array.isArray(data?.items)) {
      records = data.items;
    } else if (Array.isArray(data?.content)) {
      records = data.content;
    } else if (data && typeof data === 'object') {
      // Single object → single-row CSV
      records = [data];
    }

    const fieldsToKeep = [
      'deviceId',
      'sendTimestamp',
      'no',
      'no2',
      'o3',
      'pm1',
      'pm25',
      'pm10',
    ];
    const flatRows = records.map((item) => {
      const filteredItem = {};
      fieldsToKeep.forEach((field) => {
        if (item[field] !== undefined) {
          filteredItem[field] = item[field];
        }
      });
      return filteredItem;
    });

    // Compute stable header set
    const headerSet = new Set<string>();
    flatRows.forEach((r) => Object.keys(r).forEach((k) => headerSet.add(k)));
    const headers = Array.from(headerSet);

    // Build CSV text (Excel-kompatibel)
    const csvLines: string[] = [];
    csvLines.push('sep=,');
    csvLines.push(headers.join(','));
    // eslint-disable-next-line no-restricted-syntax
    for (const row of flatRows) {
      csvLines.push(headers.map((h) => this.csvEscape(row[h] ?? '')).join(','));
    }
    return csvLines.join('\r\n');
  }

  private csvEscape(val: string | number | boolean | null | undefined): string {
    const v: any = val;
    let s: string;
    if (v === null || v === undefined) {
      s = '';
    } else if (typeof v === 'boolean') {
      s = v ? 'true' : 'false';
    } else {
      s = String(v);
    }
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  }
}
